import {
    Router,
    history,
} from 'backbone';
import $ from 'jquery';

import Settings from 'source/lib/settings_model';
import stringToUrl from 'source/lib/utils/string_to_url';
import messages from 'messages';

import NotFoundView from './404';

const IDRouter = Router.extend({
    routes: {
        'signin/': 'signin',
        'confirmation/': 'confirm',
        'signin/external/unbound/': 'unbound',
        'recovery/': 'recover',
        '*handleMissingRoute': 'handle404',
    },
    views: {},
    initialize() {
        this.setTitle();
        const baseUrl = Settings.get('urls.BaseIDUrl', '/id/');

        history.start({
            root: baseUrl,
            pushState: true,
        });
        this.on('changelanguage', () => {
            this.viewBundle({ reload: true, router: this });
            this.setTitle();
        });
    },
    signin() {
        const url = stringToUrl(window.location.href);
        const confirmation = url.searchParams.get('confirmation');
        const singleExternalMode = Settings.get('Authentication.ExternalProviders', []).length === 1
            && !Settings.get('Authentication.BasicLoginEnabled');
        const externalLoginUrl = Settings.get('Authentication.ExternalLoginUrl', '');
        const externalNextParamName = Settings.get('Authentication.externalLoginNextParamName', 'next');
        const isAuthenticated = Settings.get('Request.AccountInfo.IsAuthenticated');
        const IsInsecurelyAuthenticated = Settings.get('Request.AccountInfo.IsInsecurelyAuthenticated');
        const externalLoginMode = externalLoginUrl !== '';
        if (confirmation && isAuthenticated && singleExternalMode) {
            const returnUrl = new URL(Settings.get('Authentication.LoginUrl'), window.location.origin);
            returnUrl.searchParams.append('next', Settings.get('Request.NextUrl'));
            const forceCreate = url.searchParams.get('force_create');
            if (forceCreate) {
                returnUrl.searchParams.append('force_create', '1');
            }

            const redirectUrl = new URL(Settings.get('Authentication.LogoutTriggerUrl'), window.location.origin);
            redirectUrl.searchParams.append('next', returnUrl.toString());
            this.routeByUrl(redirectUrl.toString());
            return;
        }
        if (isAuthenticated || IsInsecurelyAuthenticated) {
            const accountEmail = Settings.get('Request.AccountInfo.Email');
            const teleportRequired = Settings.get('Request.AccountInfo.TeleportRequired');
            const recoveryRequired = Settings.get('Request.AccountInfo.RecoveryRequired');
            let nextUrl;
            if (recoveryRequired) {
                nextUrl = Settings.get('Realm.TeleportRecoveryLink');
            } else if (teleportRequired) {
                nextUrl = Settings.get('Realm.ExternalTeleportLink');
            } else if (accountEmail) {
                nextUrl = Settings.get('Request.NextUrl');
            } else {
                nextUrl = Settings.get('Request.IncompleteUrl');
            }
            this.routeByUrl(nextUrl);
            return;
        }
        if (externalLoginMode && !url.searchParams.get('external_status_state')) {
            const redirectUrl = new URL(externalLoginUrl);
            const external = url.searchParams.get('external');
            if (external) {
                redirectUrl.searchParams.append('external', external);
            }

            redirectUrl.searchParams.append(externalNextParamName, Settings.get('Request.NextUrl'));
            this.routeByUrl(redirectUrl.toString());
            return;
        }
        if (singleExternalMode && !url.searchParams.get('external') && !url.searchParams.get('external_status_state')) {
            const external = Settings.get('Authentication.ExternalProviders', [])[0];
            const externalUrl = new URL(Settings.get('Authentication.LoginUrl'), window.location.origin);
            externalUrl.searchParams.append('external', external);
            externalUrl.searchParams.append('next', Settings.get('Request.NextUrl'));
            const forceCreate = url.searchParams.get('force_create');
            if (forceCreate) {
                externalUrl.searchParams.append('force_create', '1');
            }

            this.routeByUrl(externalUrl.toString());
            return;
        }
        let handler;
        if (singleExternalMode || externalLoginMode) {
            handler = import('./single_external.js' /* webpackChunkName: "single_external" */);
        } else {
            handler = import('./index.js' /* webpackChunkName: "loginview" */);
        }
        this.initializeRoute(handler, messages.texts.default_page_title, '–');
    },
    confirm() {
        const isAuthenticated = Settings.get('Request.AccountInfo.IsAuthenticated');
        if (!isAuthenticated) {
            this.routeByUrl(Settings.get('Authentication.LoginUrl'));
            return;
        }

        let OIDUrlPattern;
        try {
            const OIDUrl = Settings.get('Authentication.OIDUrl', '/id/openid/');
            OIDUrlPattern = new RegExp(`${OIDUrl}\\d+/`);
        } catch (e) {
            console.log(e);
        }
        if (OIDUrlPattern && !OIDUrlPattern.test(Settings.get('Request.TrustedNextUrl'))) {
            this.handle404();
            return;
        }
        const handler = import('./confirm.js' /* webpackChunkName: "confirm" */);
        this.initializeRoute(handler, messages.texts.confirm_page_title);
    },
    recover() {
        const handler = import('./recover.js' /* webpackChunkName: "recover" */);
        this.initializeRoute(handler);
    },
    unbound() {
        const url = stringToUrl(window.location.href);
        const flow = url.searchParams.get('flow');
        const isAuthenticated = Settings.get('Request.AccountInfo.IsAuthenticated');
        const nextLocation = Settings.get('Request.NextLocation');
        const isGameFlow = Settings.get('Authentication.GameEnabled') && flow === 'game';
        if (!isAuthenticated || !isGameFlow) {
            this.routeByUrl(nextLocation);
            return;
        }

        /*
            TODO: remove in scope of WGNI-1257
            temporary fix made for demo account compatibility:
                Account don't have external email.
                Skip bind step and redirect to client
        */
        const accountEmail = Settings.get('Request.AccountInfo.Email');
        if (!accountEmail) {
            const nextUrl = stringToUrl(nextLocation);
            nextUrl.searchParams.delete('external');
            this.routeByUrl(nextUrl.toString());
            return;
        }

        const handler = import('./unbound.js' /* webpackChunkName: "unbound" */);
        this.initializeRoute(handler);
    },
    routeByUrl(url) {
        const baseUrl = Settings.get('urls.BaseIDUrl', '/id/');
        const jsView = $('#js-view');

        if (url.indexOf(baseUrl) === 0) {
            const nextUrl = url.substring(baseUrl.length);
            if (this.routes[nextUrl.split(/[#?]/)[0]]) {
                Settings.syncAccountState().then(() => {
                    this.navigate(nextUrl, { trigger: true });
                });
                return;
            }
        }

        if (jsView.is(':empty')) {
            jsView.addClass('hidden');
        }

        window.location.replace(url);
    },
    setTitle(title, separator = '|') {
        if (title) {
            window.document.title = `${title} ${separator} ${messages.texts.base_title}`;
        }
    },
    initializeRoute(handler, title, separator) {
        handler.then(bundle => {
            this.viewBundle = bundle.default;
            this.viewBundle({ router: this });
            this.setTitle(title, separator);
        });
    },
    handle404() {
        this.setTitle(messages.texts['404_title']);
        if (this.notFoundView) {
            this.notFoundView.render();
        } else {
            this.notFoundView = new NotFoundView({
                el: '#js-view',
            });
        }
    },
});

export default IDRouter;
